/**
 * SSO mot Sigge via Office.auth.getAccessToken.
 *
 * Flödet:
 *   1. Office.auth.getAccessToken({ allowSignInPrompt: true }) ger oss en
 *      Azure-utfärdad ID-token som har Sigge-backenden som audience.
 *   2. Vi skickar tokenen till Sigge: POST /api/integrations/microsoft/sso/
 *   3. Sigge validerar, byter via OBO mot Graph-token, returnerar Sigge-JWT.
 *
 * Tokenen cachas i sessionStorage så vi slipper SSO-prompt vid varje
 * mail-item.
 */

const ACCESS_KEY = "sigge_access";
const REFRESH_KEY = "sigge_refresh";

export interface SiggeUser {
  id: number;
  email: string;
  full_name: string;
  role: string;
}

export interface SsoResult {
  access: string;
  refresh: string;
  user: SiggeUser;
}

// Step 7: fail-closed. Tidigare fallback `https://app.sigge.io` betydde
// att ett bygget-utan-env (t.ex. dev-byggt utan att sätta env-filen)
// skickade Microsoft-tokens till PROD-backenden. Misskonfig ska bli
// uppenbar direkt, inte tyst route:a riktiga creds fel.
function _requireApiBase(): string {
  const url = import.meta.env.VITE_SIGGE_API_URL as string | undefined;
  if (!url) {
    throw new Error(
      "VITE_SIGGE_API_URL saknas i bygget — add-in:et vet inte vilken " +
        "Sigge-backend det ska prata med. Sätt env-variabeln före build."
    );
  }
  return url;
}
const SIGGE_API_URL: string = _requireApiBase();

// XSS-SENSITIV LAGRING. `sessionStorage` är läsbart för all JS som kör
// i samma origin → om någon XSS-vektor någonsin tar sig in i add-in:et
// kan en angripare exfiltrera Sigge-JWT:n härifrån. Step 8 har stängt
// de kända ytorna (mejl-HTML saneras serverside + renderas i sandboxad
// iframe i portalen), men den här komponenten bör flyttas till
// HttpOnly-cookie eller in-memory-only innan add-in:et hanterar
// extern-tenant-innehåll bredare. Lägg INTE till fler tokens här utan
// att uppdatera den planen.
export const tokenStore = {
  get access(): string | null {
    return sessionStorage.getItem(ACCESS_KEY);
  },
  set(result: SsoResult): void {
    sessionStorage.setItem(ACCESS_KEY, result.access);
    sessionStorage.setItem(REFRESH_KEY, result.refresh);
  },
  clear(): void {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  },
};

export async function signInWithOffice(
  { silent = false }: { silent?: boolean } = {}
): Promise<SsoResult> {
  // 1. Hämta Microsoft ID-token från Office.js. Failas om tenanten inte
  //    har consentat appen för SSO — fallback (dialog-login) implementeras
  //    separat om vi behöver det.
  //
  //    silent=true används av authFetch när Sigge-JWT:n har expirerat
  //    (efter t.ex. maskin-sömn över natten). Office ger då tillbaka
  //    en färsk token utan UI-prompt om Office-sessionen fortfarande
  //    är giltig; om en prompt hade behövts kastas TokenNeededError
  //    och authFetch låter felet bubbla.
  let idToken: string;
  try {
    idToken = await Office.auth.getAccessToken({
      allowSignInPrompt: !silent,
      allowConsentPrompt: !silent,
      forMSGraphAccess: false,
    });
  } catch (err) {
    throw new Error(
      `Office SSO misslyckades: ${(err as { message?: string }).message ?? err}`
    );
  }

  // 2. Skicka till Sigge för byte mot Sigge-JWT
  const res = await fetch(`${SIGGE_API_URL}/api/integrations/microsoft/sso/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  const result = (await res.json()) as SsoResult;
  tokenStore.set(result);
  return result;
}

/**
 * Wrappar fetch med automatisk token-refresh vid 401. Skäl: Sigge-
 * JWT:n har kort livslängd; efter t.ex. över-natten-sömn på maskinen
 * är den utgången och backend kastar "Givet token är inte giltigt
 * för någon tokentyp". Utan wrapper skulle juristen behöva klicka
 * Logga in igen — även om Office-sessionen fortfarande är giltig.
 *
 * Flödet:
 *   1. Kör fetch med nuvarande access-token.
 *   2. Om 401: tystcasa signInWithOffice({silent:true}) → ny SSO →
 *      ny Sigge-JWT sparas i tokenStore.
 *   3. Kör om fetch en gång med den nya token:en.
 *   4. Om även retry:t ger 401 (eller silent-SSO failade) → returnera
 *      det ursprungliga 401-svaret så anroparen kan visa fel.
 */
export async function authFetch(
  url: string,
  init: RequestInit = {}
): Promise<Response> {
  const call = (): Promise<Response> => {
    const tok = tokenStore.access;
    const headers = new Headers(init.headers ?? {});
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
    return fetch(url, { ...init, headers });
  };
  let res = await call();
  if (res.status !== 401) return res;

  try {
    await signInWithOffice({ silent: true });
  } catch {
    return res;
  }
  return await call();
}

export function getApiBase(): string {
  return SIGGE_API_URL;
}
